import { useTranslation } from "react-i18next";
import { dayLabel, draftRoundTypes, programToDraft, workoutToSlotDrafts, type Program, type Workout } from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import { ModelSaveDialog, type ModelSaveDialogProps } from "./ModelSaveDialog";

type SourceProps = Omit<ModelSaveDialogProps, "source">;

// #354: «Save as model» on the caller's own program, all its days and rounds.
export function SaveAsModelDialog({ program, ...props }: SourceProps & { program: Program }) {
  const lang = useLocale();
  const source = () => {
    const draft = programToDraft(program, props.unit, lang);
    return {
      days: draft.sessions,
      roundTypes: draftRoundTypes(draft),
      sessionsPerWeek: draft.sessionsPerWeek,
      focus: draft.focus,
      description: draft.description,
      locale: draft.editLocale ?? null,
      dayLabel: (d: number) => dayLabel(draft, d),
    };
  };
  return <ModelSaveDialog {...props} source={source} />;
}

// #359: «Save as model» on a logged session: one day, one round, a row per
// exercise from its heaviest set (workoutToSlotDrafts). `defaultName` is the
// session's name, and the day's.
// #368: N sessions from the diary's selection, one day each in the order
// they were picked, «Day n» in the summary. `names` are the days' names.
export function SaveSessionAsModelDialog({
  workouts,
  names,
  ...props
}: SourceProps & { workouts: readonly Workout[]; names?: readonly string[] }) {
  const lang = useLocale();
  const { t } = useTranslation();
  const many = workouts.length > 1;
  const source = () => {
    let warmUps = 0;
    const bodyweight = new Set<string>();
    const days = workouts.map((w, i) => {
      const out = workoutToSlotDrafts(w, props.unit, props.catalog ?? undefined);
      warmUps += out.warmUps;
      for (const slug of out.bodyweight) bodyweight.add(slug);
      return { name: names?.[i] ?? props.defaultName, slots: out.slots };
    });
    return {
      days,
      roundTypes: ["normal" as const],
      sessionsPerWeek: workouts.length,
      description: "",
      // A session declares no language: the athlete's own is the one they write in.
      locale: lang,
      warmUps,
      bodyweight: [...bodyweight],
      ...(many
        ? { dayLabel: (d: number) => t("mobile.workouts.dayN", { n: d + 1 }) }
        : {}),
    };
  };
  return <ModelSaveDialog {...props} source={source} />;
}
